import {
  AutoAwesome,
  EventOutlined,
  TextSnippetOutlined,
} from "@mui/icons-material";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  DialogTitle,
  Grid2,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Button from "../../../components/buttons/Button";
import BarChartWithErrors from "../../../components/graphs/BarChartWithErrors";
import TwoCricles from "../../../components/graphs/TwoCircles";
import Dialog from "../../../components/misc/Dialog";
import { useCase } from "../../../contexts/CaseContext";
import Translations from "../../../lang/en/pages.json";
import {
  formatBoolean,
  formatDateUTC,
  formatDateWithSlashes,
  formatNumberWithSuffix,
  formatTagsArrayFromEnum,
} from "../../../utils";
import {
  CaseInformationAccidentType,
  CaseInformationNatureOfInjury,
  literalCaseInformationAccidentType,
  literalInformationNatureOfInjury,
} from "../types";
import { useApi } from "../../../contexts/ApiContext";

const StyledQnaTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#9e9e9e", // Gray border
    },
    "&:hover fieldset": {
      borderColor: "#757575", // Darker gray on hover
    },
    "&.Mui-focused fieldset": {
      borderColor: "#757575 !important", // Keep gray when focused
    },
  },
  "& .MuiInputLabel-root": {
    color: "#9e9e9e", // Gray label
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#757575 !important", // Keep gray when focused
  },
  "& .MuiFormLabel-root.Mui-focused": {
    color: "#757575 !important", // Additional selector for focused state
  },
});

interface OverviewGridItemBase {
  icon: React.ReactNode;
  title: string;
  id: string;
}

interface OverviewGridItemWithText extends OverviewGridItemBase {
  text: string;
  tags?: never;
}

interface OverviewGridItemWithTags extends OverviewGridItemBase {
  tags: string[];
  text?: never;
}
type OverviewGridItem = OverviewGridItemWithText | OverviewGridItemWithTags;

const GridDisplay: React.FC<{ items: OverviewGridItem[] }> = ({ items }) => {
  return (
    <Card id="grid" sx={{ backgroundColor: "white" }}>
      <Grid2
        columnSpacing={0}
        rowSpacing={0}
        container
        spacing={0}
        justifyContent="center"
        alignItems="center"
      >
        {items.map((item, index) => (
          <Grid2
            id={`grid-${item.id}`}
            size={{ xs: 12, sm: 6, md: 4 }}
            key={index}
            borderRight={index % 3 === 2 ? 0 : 1}
            borderBottom={index < 6 ? 1 : 0}
            borderColor={"#DEDEDE"}
            display="flex"
            padding={2}
            boxSizing={"border-box"}
            flexDirection="column"
            style={{ minHeight: 149 }}
            textAlign="center"
          >
            <Stack spacing={1}>
              <Box>{item.icon}</Box>
              <Typography fontWeight={"bold"} variant="body2" component="div">
                {item.title}
              </Typography>
              <Typography
                variant="body2"
                color={"#404943"}
                component="div"
                mt={1}
              >
                {item.text}
              </Typography>
              {item.tags && (
                <Stack
                  direction={"row"}
                  flexWrap={"wrap"}
                  spacing={0}
                  mt={2}
                  gap={0.5}
                >
                  {item.tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={
                        <Typography fontSize={11} variant="caption">
                          {tag}
                        </Typography>
                      }
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Stack>
              )}
            </Stack>
          </Grid2>
        ))}
      </Grid2>
    </Card>
  );
};

const QnADialog: React.FC<{
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose }) => {
  const [inputText, setInputText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(e.target.value);
  };
  const { caseMeta, currentOutcome, revisionMeta } = useCase() || {};
  const [isEligible, setIsEligible] = useState<boolean | null>(false);

  const { client } = useApi();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      {isEligible === null && (
        <CircularProgress
          size={24}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        />
      )}

      {isEligible === false && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "200px",
            textAlign: "center",
            py: 4,
          }}
        >
          <AutoAwesome
            sx={{
              fontSize: 48,
              color: "#9e9e9e",
              mb: 2,
            }}
          />
          <Typography
            variant="h5"
            sx={{
              color: "#757575",
              mb: 1,
            }}
          >
            {
              Translations.pages.dashboard_page.tabs.overview.qna.coming_soon
                .title
            }
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {
              Translations.pages.dashboard_page.tabs.overview.qna.coming_soon
                .description
            }
          </Typography>
        </Box>
      )}
      {isEligible === true && (
        <>
          <DialogTitle>
            {Translations.pages.dashboard_page.tabs.overview.qna.ask_question}
          </DialogTitle>

          <StyledQnaTextField
            autoFocus
            margin="dense"
            label={
              Translations.pages.dashboard_page.tabs.overview.qna.placeholder
            }
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={inputText}
            onChange={handleInputChange}
            disabled={false}
          />
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", mt: 2, mb: 2 }}
          >
            <Button
              onClick={() => {}}
              color="primary"
              variant="purple"
              disabled={!inputText.trim() || isLoading}
              sx={{ width: "auto", minWidth: "100px", px: 1.5 }}
            >
              {Translations.pages.dashboard_page.tabs.overview.qna.send_button}
            </Button>
          </Box>

          {/* Loading Indicator */}
          {isLoading && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mt: 2,
                mb: 2,
              }}
            >
              <CircularProgress size={40} />
              <Typography mt={2} variant="body2" color="text.secondary">
                {Translations.pages.dashboard_page.tabs.overview.qna.loading}
              </Typography>
            </Box>
          )}

          {/* Error Message */}
          {error && (
            <Box
              mt={2}
              p={2}
              bgcolor="#fff3f0"
              borderRadius={1}
              border="1px solid #ffcccc"
            >
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          {/* Response Box */}
          {response && (
            <Box mt={2} p={2} bgcolor="#5555555" borderRadius={1}>
              <Typography variant="body1">{response}</Typography>
            </Box>
          )}
        </>
      )}
    </Dialog>
  );
};

const CaseSummary: React.FC<{
  name: string;
  category: string;
  onClick: () => void;
}> = ({ name, category, onClick }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  return (
    <>
      <Card id="case_info" sx={{ backgroundColor: "white", py: 2, px: 8 }}>
        <Stack
          direction="row"
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Stack maxWidth={"60%"} spacing={1}>
            <Typography
              variant={"h5"}
              color={"#171D19"}
              fontWeight={"medium"}
              fontSize={18}
              height={66}
              maxWidth={"100%"}
              overflow={"hidden"}
              whiteSpace={"wrap"}
              textOverflow={"ellipsis"}
            >
              {name}
            </Typography>
            <Typography
              color={"#3C6472"}
              variant={"caption"}
              fontWeight={"light"}
              fontSize={14}
            >
              {category}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={2}>
            <Box>
              <Button onClick={onClick} variant={"purple"}>
                <Stack direction={"row"} spacing={1}>
                  <TextSnippetOutlined fontSize="small" />
                  <Typography fontSize={14}>
                    {Translations.pages.dashboard_page.summary_block.title}
                  </Typography>
                </Stack>
              </Button>
            </Box>
            <Box>
              <Button onClick={() => setIsDialogOpen(true)} variant={"purple"}>
                <Stack direction={"row"} spacing={1}>
                  <AutoAwesome fontSize="small" />
                  <Typography fontSize={14}>
                    {
                      Translations.pages.dashboard_page.tabs.overview.qna
                        .toggle_button
                    }
                  </Typography>
                </Stack>
              </Button>
            </Box>
          </Stack>
        </Stack>
      </Card>

      <QnADialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} />

      {/* Dialog with Coming Soon content */}
    </>
  );
};

interface LegalOutcomesChartItem {
  percentage: number;
  error: [number, number];
  tooltip: string;
}

const LegalOutcomesChart: React.FC<{
  settlement: LegalOutcomesChartItem;
  trial: LegalOutcomesChartItem;
  dismissal: LegalOutcomesChartItem;
}> = ({ settlement, trial, dismissal }) => {
  return (
    <Card id="outcome_chart" sx={{ backgroundColor: "white", p: 4 }}>
      <BarChartWithErrors
        width={400}
        height={200}
        showXAxis={true}
        showYAxis={true}
        YAxisLabel={
          Translations.pages.dashboard_page.tabs.overview.case_outcome_block
            .y_axis.label
        }
        data={[
          {
            name: Translations.pages.dashboard_page.tabs.overview
              .case_outcome_block.x_axis.settlement,
            value: settlement.percentage,
            error: settlement.error,
            tooltipContent: settlement.tooltip,
          },
          {
            name: Translations.pages.dashboard_page.tabs.overview
              .case_outcome_block.x_axis.trial,
            value: trial.percentage,
            error: trial.error,
            tooltipContent: trial.tooltip,
          },
          {
            name: Translations.pages.dashboard_page.tabs.overview
              .case_outcome_block.x_axis.dismissal,
            value: dismissal.percentage,
            error: dismissal.error,
            tooltipContent: dismissal.tooltip,
          },
        ]}
      />
    </Card>
  );
};

const ComparativeCaseAnalysis: React.FC<{
  totalCases: number;
  relevantCases: number;
}> = ({ totalCases, relevantCases }) => (
  <Card id="relevance_analysis" sx={{ backgroundColor: "white", padding: 2 }}>
    <Typography variant="caption" fontWeight={"light"}>
      {
        Translations.pages.dashboard_page.tabs.overview
          .comparative_case_analysis.label
      }
    </Typography>
    <TwoCricles number1={totalCases} number2={relevantCases} />
  </Card>
);

// const CaseSummaryDialog: React.FC<{
//   open: boolean;
//   onClose: () => void;
//   summary: string;
// }> = ({ open, onClose, summary }) => {
//   return (
//     <Dialog open={open} onClose={onClose} maxWidth={700}>
//       <Stack alignItems={"center"} justifyContent={"center"} spacing={2}>
//         <SummarizeOutlined />
//         <Typography variant={"h6"} fontWeight={"medium"}>
//           {Translations.pages.dashboard_page.dialogs.summary.title}
//         </Typography>
//         <Typography fontSize={"body1"} color={"#404943"}>
//           {summary}
//         </Typography>
//       </Stack>
//     </Dialog>
//   );
// };

const CaseSummaryDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  summary: string;
}> = ({ open, onClose, summary }) => {
  // Function to convert markdown to HTML
  const renderMarkdown = (text: string): string => {
    if (!text) return "";

    // Replace markdown patterns with HTML
    let html = text
      // Headers
      .replace(/^### (.*$)/gim, "<h3>$1</h3>")
      .replace(/^## (.*$)/gim, "<h3>$1</h3>")
      .replace(/^# (.*$)/gim, "<h2>$1</h2>")

      // Bold and italic
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.*?)\*/g, "<em>$1</em>")

      // Links
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')

      // Lists
      .replace(/^\s*\n\* (.*)/gm, "<ul>\n<li>$1</li>")
      .replace(/^\* (.*)/gm, "<li>$1</li>")
      .replace(/^\s*\n- (.*)/gm, "<ul>\n<li>$1</li>")
      .replace(/^- (.*)/gm, "<li>$1</li>")
      .replace(/^\s*\n([0-9]+)\. (.*)/gm, "<ol>\n<li>$2</li>")
      .replace(/^([0-9]+)\. (.*)/gm, "<li>$2</li>")

      // Paragraphs
      .replace(/^\s*(\n)?(.+)/gm, function (m: string): string {
        return /\<(\/)?(h|ul|ol|li|blockquote|pre|img)/.test(m)
          ? m
          : "<p>" + m + "</p>";
      })

      // Line breaks
      .replace(/\n/g, "<br />");

    return html;
  };
  return (
    <Dialog
      minHeight={"none"}
      fullWidth
      maxWidth={700}
      onClose={onClose}
      open={open}
      sx={{
        "& .MuiDialog-paper": {
          width: "90%",
          maxWidth: "1200px",
        },
      }}
    >
      <Box
        sx={{
          marginTop: "16px",
          "& h1": {
            fontSize: "2em",
            fontWeight: "bold",
            marginBottom: "0.5em",
          },
          "& h2": {
            fontSize: "1.5em",
            fontWeight: "bold",
            marginBottom: "0.5em",
          },
          "& h3": {
            fontSize: "1.17em",
            fontWeight: "bold",
            marginBottom: "0.5em",
          },
          "& p": { marginBottom: "1em" },
          "& a": { color: "#1976d2", textDecoration: "none" },
          "& a:hover": { textDecoration: "underline" },
          "& ul, & ol": { paddingLeft: "2em", marginBottom: "1em" },
          "& li": { marginBottom: "0.5em" },
        }}
        dangerouslySetInnerHTML={{ __html: renderMarkdown(summary) }}
      />
    </Dialog>
  );
};

const Overview: React.FC = () => {
  const { caseMeta, currentOutcome } = useCase() || {};
  const [caseSummaryDialogOpen, setCaseSummaryDialogOpen] = useState(false);

  const openCaseSummaryDialog = () => {
    setCaseSummaryDialogOpen(true);
  };

  const items = [
    {
      id: "accident_type",
      icon: <EventOutlined />,
      title:
        Translations.pages.dashboard_page.tabs.overview.grid.accident_type
          .label,
      tags: formatTagsArrayFromEnum<CaseInformationAccidentType>(
        currentOutcome?.information?.accident_type ?? [],
        literalCaseInformationAccidentType
      ),
    },
    {
      id: "accident_location",
      icon: <EventOutlined />,
      title:
        Translations.pages.dashboard_page.tabs.overview.grid.accident_place
          .label,
      text: currentOutcome?.information?.place_of_accident,
    },
    {
      id: "accident_date",
      icon: <EventOutlined />,
      title:
        Translations.pages.dashboard_page.tabs.overview.grid.accident_date
          .label,
      text: currentOutcome?.information?.accident_date
        ? formatDateUTC(currentOutcome.information.accident_date)
        : "N/A",
    },
    {
      id: "nature_of_injury",
      icon: <EventOutlined />,
      title:
        Translations.pages.dashboard_page.tabs.overview.grid.nature_of_injury
          .label,
      tags: formatTagsArrayFromEnum<CaseInformationNatureOfInjury>(
        currentOutcome?.information?.nature_of_injury ?? [],
        literalInformationNatureOfInjury
      ),
    },
    {
      id: "claimed_compensation",
      icon: <EventOutlined />,
      title:
        Translations.pages.dashboard_page.tabs.overview.grid.compenstation
          .label,
      text: currentOutcome?.information?.compensation
        ? formatNumberWithSuffix(currentOutcome?.information?.compensation)
        : "N/A",
    },
    {
      id: "filing_date",
      icon: <EventOutlined />,
      title:
        Translations.pages.dashboard_page.tabs.overview.grid.filing_date.label,
      text: currentOutcome?.information?.filing_date
        ? formatDateUTC(currentOutcome.information.filing_date)
        : "N/A",
    },
    {
      id: "jurisdiction",
      icon: <EventOutlined />,
      title:
        Translations.pages.dashboard_page.tabs.overview.grid.jurisdiction.label,
      text: currentOutcome?.information?.jurisdiction,
    },
    {
      id: "policy_limit",
      icon: <EventOutlined />,
      title:
        Translations.pages.dashboard_page.tabs.overview.grid.policy_limit.label,
      text: currentOutcome?.information?.policy_limit
        ? `$${formatNumberWithSuffix(
            currentOutcome?.information?.policy_limit
          )}`
        : "N/A",
    },
    {
      id: "insurance_coverage_claimed",
      icon: <EventOutlined />,
      title:
        Translations.pages.dashboard_page.tabs.overview.grid
          .insurance_coverage_claimed.label,
      text: formatBoolean(!!currentOutcome?.information?.insurance_coverage),
    },
  ];

  const errorValue = (
    key: "settlement" | "trial" | "dismissal"
  ): [number, number] => {
    return [
      (currentOutcome?.predictions?.resolution_likelihood?.[key]
        ?.probability_percent ?? 0) -
        (currentOutcome?.predictions?.resolution_likelihood?.[key]
          .confidence_interval?.min ?? 0),
      (currentOutcome?.predictions?.resolution_likelihood?.[key]
        .confidence_interval?.max ?? 0) -
        (currentOutcome?.predictions?.resolution_likelihood?.[key]
          .probability_percent ?? 0),
    ];
  };

  return (
    <Stack spacing={2} sx={{ minHeight: "100%" }} alignItems="flex-end">
      <Stack direction={"row"} spacing={2}>
        <Stack id="summary-and-grid" spacing={2} height="100%">
          <CaseSummary
            onClick={openCaseSummaryDialog}
            name={caseMeta?.name ?? ""}
            category={[caseMeta?.category, caseMeta?.sub_category]
              .filter(Boolean)
              .join(" / ")}
          />
          <GridDisplay items={items as any} />
        </Stack>
        <Stack spacing={2} height="100%">
          {currentOutcome?.predictions && (
            <LegalOutcomesChart
              trial={{
                percentage:
                  currentOutcome?.predictions.resolution_likelihood.trial
                    .probability_percent ?? 0,
                error: errorValue("trial"),
                tooltip:
                  JSON.stringify(
                    currentOutcome?.predictions.resolution_likelihood.trial
                      .explanation
                  ).replace(/"/g, "") ?? "",
              }}
              settlement={{
                percentage:
                  currentOutcome?.predictions.resolution_likelihood.settlement
                    .probability_percent ?? 0,
                error: errorValue("settlement"),
                tooltip:
                  JSON.stringify(
                    currentOutcome?.predictions.resolution_likelihood.settlement
                      .explanation
                  ).replace(/"/g, "") ?? "",
              }}
              dismissal={{
                percentage:
                  currentOutcome?.predictions.resolution_likelihood.dismissal
                    .probability_percent ?? 0,
                error: errorValue("dismissal"),
                tooltip:
                  JSON.stringify(
                    currentOutcome?.predictions.resolution_likelihood.dismissal
                      .explanation
                  ).replace(/"/g, "") ?? "",
              }}
            />
          )}
          <ComparativeCaseAnalysis
            totalCases={
              Translations.pages.dashboard_page.tabs.overview
                .comparative_case_analysis.highly_cases_analyzed_value
            }
            relevantCases={currentOutcome?.similar_cases?.length ?? 0}
          />
        </Stack>
      </Stack>
      <CaseSummaryDialog
        open={caseSummaryDialogOpen}
        onClose={() => setCaseSummaryDialogOpen(false)}
        summary={currentOutcome?.information?.summary ?? ""}
      />
    </Stack>
  );
};

export default Overview;
